import React, {useCallback, useEffect, useState} from "react";
import {
    Alert,
    AlertIcon,
    Box,
    Button,
    Flex,
    FormControl,
    FormLabel,
    Heading,
    Icon,
    Input,
    InputGroup,
    InputLeftElement,
    SimpleGrid,
    Text,
    useColorModeValue,
} from "@chakra-ui/react";
import {MdChevronLeft, MdChevronRight, MdFilterList, MdPeopleOutline, MdRefresh, MdSearch} from "react-icons/md";
import ColumnsTable from "./components/ColumnsTable";
import {columnsDataColumns} from "./variables/columnsData";
import Card from "components/card/Card";

export default function Settings({onDataUpdate}) {
    const [tableData, setTableData] = useState([]);
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize] = useState(15);
    const [totalPages, setTotalPages] = useState(1);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState("");
    const [filters, setFilters] = useState({
        name: "",
        email: "",
        nrp: ""
    });

    const inputBgColor = useColorModeValue("white", "gray.700");
    const inputBorderColor = useColorModeValue("gray.200", "gray.600");
    const headingColor = useColorModeValue("navy.700", "white");
    const mutedTextColor = useColorModeValue("secondaryGray.600", "secondaryGray.500");
    const filterBgColor = useColorModeValue("secondaryGray.300", "whiteAlpha.100");
    const iconBgColor = useColorModeValue("brand.100", "whiteAlpha.100");

    const fetchUsers = useCallback(async () => {
        setIsLoading(true);
        setLoadError("");

        try {
        const token = document.cookie.split('; ').find(row => row.startsWith('token=')).split('=')[1];
            const response = await fetch(
            process.env.REACT_APP_API_URL + `/v1/users?page=${pageIndex + 1}&size=${pageSize}&name=${filters.name}&nrp=${filters.nrp}&email=${filters.email}`,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }
            );

            if (!response.ok) {
                throw new Error("Failed to fetch users data");
            }

            const data = await response.json();
            if (!data.success) {
                throw new Error(data.message || "Failed to fetch users data");
            }

            setTableData(data.data.content);
            setTotalPages(data.data.totalPages);
        } catch (error) {
            setLoadError(error.message || "Unable to load user data.");
        } finally {
            setIsLoading(false);
        }
    }, [filters, pageIndex, pageSize]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    // Handle filter changes
    const handleFilterChange = (e) => {
        setFilters({...filters, [e.target.name]: e.target.value});
        setPageIndex(0);
    };

    const resetFilters = () => {
        setFilters({name: "", email: "", nrp: ""});
        setPageIndex(0);
    };

    const hasActiveFilters = Object.values(filters).some(Boolean);

    return (
        <Box pt={{base: "130px", md: "80px", xl: "80px"}}>
            <Card mb="24px" p={{base: "20px", md: "24px"}}>
                <Flex direction={{base: "column", md: "row"}} justify="space-between" align={{base: "flex-start", md: "center"}} gap={5}>
                    <Flex align="center" gap={4}>
                        <Flex w="48px" h="48px" borderRadius="16px" bg={iconBgColor} align="center" justify="center">
                            <Icon as={MdPeopleOutline} color="brand.500" boxSize={6}/>
                        </Flex>
                        <Box>
                            <Heading as="h1" size="lg" color={headingColor}>Master User</Heading>
                            <Text color={mutedTextColor} mt={1} fontSize="sm">
                                Kelola data user dan additional company dalam satu workspace.
                            </Text>
                        </Box>
                    </Flex>
                    <Button variant="light" size="sm" leftIcon={<Icon as={MdRefresh}/>} onClick={fetchUsers} isLoading={isLoading}>
                        Refresh data
                    </Button>
                </Flex>
            </Card>

            <Card mb="24px" p={{base: "20px", md: "24px"}}>
                <Flex justify="space-between" align={{base: "flex-start", sm: "center"}} direction={{base: "column", sm: "row"}} gap={3} mb={5}>
                    <Flex align="center" gap={2}>
                        <Icon as={MdFilterList} color="brand.500" boxSize={5}/>
                        <Box>
                            <Heading as="h2" size="sm" color={headingColor}>Filter user</Heading>
                            <Text color={mutedTextColor} fontSize="sm">Persempit daftar berdasarkan informasi user.</Text>
                        </Box>
                    </Flex>
                    <Button variant="ghost" size="sm" onClick={resetFilters} isDisabled={!hasActiveFilters}>
                        Reset filter
                    </Button>
                </Flex>
                <SimpleGrid columns={{base: 1, md: 3}} spacing={4} bg={filterBgColor} p={{base: 3, md: 4}} borderRadius="16px">
                    <FormControl>
                        <FormLabel htmlFor="user-name-filter" fontSize="sm" color={headingColor}>Nama</FormLabel>
                        <InputGroup>
                            <InputLeftElement pointerEvents="none"><Icon as={MdSearch} color="secondaryGray.500"/></InputLeftElement>
                            <Input
                                id="user-name-filter"
                                placeholder="Cari nama"
                                name="name"
                                value={filters.name}
                                onChange={handleFilterChange}
                                bg={inputBgColor}
                                borderColor={inputBorderColor}
                            />
                        </InputGroup>
                    </FormControl>
                    <FormControl>
                        <FormLabel htmlFor="user-email-filter" fontSize="sm" color={headingColor}>Email</FormLabel>
                        <InputGroup>
                            <InputLeftElement pointerEvents="none"><Icon as={MdSearch} color="secondaryGray.500"/></InputLeftElement>
                            <Input
                                id="user-email-filter"
                                placeholder="Cari email"
                                name="email"
                                value={filters.email}
                                onChange={handleFilterChange}
                                bg={inputBgColor}
                                borderColor={inputBorderColor}
                            />
                        </InputGroup>
                    </FormControl>
                    <FormControl>
                        <FormLabel htmlFor="user-nrp-filter" fontSize="sm" color={headingColor}>NRP</FormLabel>
                        <InputGroup>
                            <InputLeftElement pointerEvents="none"><Icon as={MdSearch} color="secondaryGray.500"/></InputLeftElement>
                            <Input
                                id="user-nrp-filter"
                                placeholder="Cari NRP"
                                name="nrp"
                                value={filters.nrp}
                                onChange={handleFilterChange}
                                bg={inputBgColor}
                                borderColor={inputBorderColor}
                            />
                        </InputGroup>
                    </FormControl>
                </SimpleGrid>
            </Card>

            {loadError ? (
                <Card p={{base: "20px", md: "24px"}}>
                    <Alert status="error" borderRadius="12px" alignItems="flex-start">
                        <AlertIcon mt={1}/>
                        <Box>
                            <Text fontWeight="700">Data user tidak dapat dimuat</Text>
                            <Text fontSize="sm" mt={1}>{loadError}</Text>
                            <Button mt={4} size="sm" colorScheme="red" onClick={fetchUsers} leftIcon={<Icon as={MdRefresh}/>}>Coba lagi</Button>
                        </Box>
                    </Alert>
                </Card>
            ) : (
                <>
                    <ColumnsTable columnsData={columnsDataColumns} tableData={tableData} onDataUpdate={fetchUsers} isLoading={isLoading}/>
                    <Card p={{base: "16px", md: "20px"}}>
                        <Flex direction={{base: "column", sm: "row"}} justify="space-between" align="center" gap={4}>
                            <Button
                                variant="light"
                                size="sm"
                                leftIcon={<Icon as={MdChevronLeft}/>}
                                onClick={() => setPageIndex((prev) => Math.max(prev - 1, 0))}
                                isDisabled={pageIndex === 0 || isLoading}
                            >
                                Sebelumnya
                            </Button>
                            <Text color={mutedTextColor} fontSize="sm" textAlign="center">
                                Halaman <Text as="span" color={headingColor} fontWeight="700">{pageIndex + 1}</Text> dari <Text as="span" color={headingColor} fontWeight="700">{totalPages}</Text>
                            </Text>
                            <Button
                                variant="light"
                                size="sm"
                                rightIcon={<Icon as={MdChevronRight}/>}
                                onClick={() => setPageIndex((prev) => Math.min(prev + 1, totalPages - 1))}
                                isDisabled={pageIndex + 1 === totalPages || isLoading}
                            >
                                Berikutnya
                            </Button>
                        </Flex>
                    </Card>
                </>
            )}
        </Box>
    );
}
